// actions
import { gasSaving } from '@tesla/gas-savings';
import { createErrorMsgError } from '../../ErrorMsg/actions';

// helpers
import Client from '../../../lib/Client';
import { makeActionCreator, fixFuelEfficiencyData } from '../../../lib/Utils';
import {
  litersPer100kmToKmPerLiter,
  mileToKM,
  mpgToLitersPer100km,
  pricePerGallonToPricePerLiter,
} from '../../../lib/ConversionUtils';

export const FUEL_EFFICIENCY_FETCH_REQUEST = 'FUEL_EFFICIENCY_FETCH_REQUEST';
const fetchFuelEfficiencyRequest = makeActionCreator(
  FUEL_EFFICIENCY_FETCH_REQUEST,
);

export const FUEL_EFFICIENCY_FETCH_SUCCESS = 'FUEL_EFFICIENCY_FETCH_SUCCESS';
const fetchFuelEfficiencySuccess = makeActionCreator(
  FUEL_EFFICIENCY_FETCH_SUCCESS,
  'cachedIncentiveData',
);

export const FUEL_EFFICIENCY_FETCH_FAILURE = 'FUEL_EFFICIENCY_FETCH_FAILURE';
const fetchFuelEfficiencyFailure = makeActionCreator(
  FUEL_EFFICIENCY_FETCH_FAILURE,
  'error',
);

export const GAS_SAVINGS_UPDATE = 'GAS_SAVINGS_UPDATE';
const updateGasSavingsData = makeActionCreator(
  GAS_SAVINGS_UPDATE,
  'efficiencyDataReady',
  'fuelEfficiency',
);

/**
 *  return trip Data object for gas savings calculation. All returned values are
 *  metric to streamline calculation.
 */
export function constructTripData(
  fuelEfficiency,
  distanceInMiles,
  countryCode,
  modelCode,
  ppuCostPerUnit,
) {
  return {
    modelCode,
    distanceInKM: mileToKM(distanceInMiles),
    fuelEfficiencyinLiterPer100km:
      countryCode === 'US'
        ? mpgToLitersPer100km(fuelEfficiency.imperial)
        : fuelEfficiency.metric,
    costOfGasPerLiter:
      countryCode === 'US'
        ? pricePerGallonToPricePerLiter(fuelEfficiency.gasCostPerUnit)
        : fuelEfficiency.gasCostPerUnit,
    energyEfficiencyInKWHPer100KM: 0.18548,
    ppuCostPerKWH: ppuCostPerUnit,
    countryCode,
  };
}

/**
 * @description
 * @returns {number}
 */
export function localizeFuelEfficiencyVal(fuelEfficiency, unitSystem) {
  let localizedFuelEfficiencyVal;
  const distanceUnit = unitSystem.distance;

  if (unitSystem.variant === 'default') {
    localizedFuelEfficiencyVal = fuelEfficiency[distanceUnit];
  } else if (unitSystem.variant === 'alt') {
    localizedFuelEfficiencyVal = litersPer100kmToKmPerLiter(
      fuelEfficiency[distanceUnit],
    );
  }

  return localizedFuelEfficiencyVal;
}

/**
 * Disclaimer values are localized.
 */
export function constructDisclaimerVal(
  fuelEfficiency,
  unitSystem,
  ppuCostPerUnit,
) {
  return {
    fuelEfficiencyVal: localizeFuelEfficiencyVal(fuelEfficiency, unitSystem),
    ppuCostPerUnit: ppuCostPerUnit.toFixed(2),
    gasCostPerUnit: fuelEfficiency.gasCostPerUnit.toFixed(2),
  };
}

export function updateGasSavings(countryCode, modelCode) {
  return (dispatch, getState) => {
    const currentState = getState();
    let tripGasSaving;
    let disclaimerValues;
    dispatch(fetchFuelEfficiencyRequest(countryCode, modelCode));

    function updateAllGasSavingsData(incentivesData) {
      if (incentivesData) {
        const tripData = constructTripData(
          {
            metric: incentivesData.variables.fuel_efficiency_metric,
            imperial: incentivesData.variables.fuel_efficiency_imperial,
            gasCostPerUnit: incentivesData.variables.fuel_price,
          },
          currentState.Directions.distance,
          countryCode,
          modelCode,
          incentivesData.variables.supercharger_kwh_price,
        );

        disclaimerValues = constructDisclaimerVal(
          {
            metric: incentivesData.variables.fuel_efficiency_metric,
            imperial: incentivesData.variables.fuel_efficiency_imperial,
            gasCostPerUnit: incentivesData.variables.fuel_price,
          },
          currentState.App.unit.system,
          incentivesData.variables.supercharger_kwh_price,
        );

        if (currentState.Directions.directionsLoaded) {
          const isPPU = modelCode === 'm3';
          const calculateMonthlySavings = !currentState.Superchargers.chargingNeeded;
          tripGasSaving = gasSaving(tripData, isPPU, calculateMonthlySavings);
        }
      }

      dispatch(updateGasSavingsData(true, { tripGasSaving, disclaimerValues }));
    }

    if (!currentState.GasSavings.cachedIncentiveData[modelCode]) {
      Client.loadFuelEfficiency(countryCode, modelCode, (err, res) => {
        let cachedIncentiveData;

        if (err) {
          const { somethingWrong } = getState().ErrorMsg.errors;
          dispatch(fetchFuelEfficiencyFailure());
          dispatch(createErrorMsgError(somethingWrong));
        } else {
          let incentivesData = res.body.incentives[0];

          // TODO: remove workaround for GB once tesla-rest-financial-services is fixed
          incentivesData = fixFuelEfficiencyData(countryCode, incentivesData);

          cachedIncentiveData = Object.assign(
            {},
            currentState.GasSavings.cachedIncentiveData,
          );
          cachedIncentiveData[modelCode] = incentivesData;

          dispatch(fetchFuelEfficiencySuccess(cachedIncentiveData));

          updateAllGasSavingsData(incentivesData);
        }
      });
    } else {
      // eslint-disable-next-line operator-linebreak
      const incentivesData =
        currentState.GasSavings.cachedIncentiveData[modelCode];
      updateAllGasSavingsData(incentivesData);
    }
  };
}
